import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MetaMasivaRepository } from 'src/app/domain/service/meta-masiva/meta-masiva-repository';
import {
  MetaMasivaDireccion,
  MetaMasivaFiltro,
  MetaMasivaRequest,
  MetaMasivaResultado,
  MetaMasivaSearchResult
} from 'src/app/domain/model/meta-masiva/meta-masiva';
import { MAX_PAGE_SIZE } from 'src/app/common/constants-ejb';

/**
 * Adaptador HTTP: HttpMetaMasivaRepository
 *
 * Endpoints (sin prefijo de versión): `GET /`, `POST /`, `PUT /{nmconsecutivo}`.
 *
 * El listado va por GET con `oficina`, `anio`, `size` y `direction` siempre presentes; el
 * `cursor` solo se envía cuando existe (sin cursor = primera página). El DNI del usuario nunca
 * viaja en la petición: el servidor lo toma de la sesión.
 */
@Injectable({ providedIn: 'root' })
export class HttpMetaMasivaRepository implements MetaMasivaRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/metas-masivas`;
  private static readonly PAGE_SIZE_MIN = 1;

  async listar(
    filtro: MetaMasivaFiltro,
    size: number,
    cursor?: string | null,
    direction: MetaMasivaDireccion = 'NEXT'
  ): Promise<MetaMasivaSearchResult> {
    this.validarPaginacion(size);

    let params = new HttpParams()
      .set('oficina', String(filtro.oficina))
      .set('anio', String(filtro.anio))
      .set('size', String(size))
      .set('direction', direction);
    if (cursor) params = params.set('cursor', cursor);

    return firstValueFrom(
      this.http.get<MetaMasivaSearchResult>(this.baseUrl, { params })
    );
  }

  async crear(request: MetaMasivaRequest): Promise<MetaMasivaResultado> {
    return firstValueFrom(
      this.http.post<MetaMasivaResultado>(this.baseUrl, request)
    );
  }

  async modificar(
    nmconsecutivo: number,
    metaMes: number
  ): Promise<MetaMasivaResultado> {
    return firstValueFrom(
      this.http.put<MetaMasivaResultado>(
        `${this.baseUrl}/${encodeURIComponent(String(nmconsecutivo))}`,
        { metaMes }
      )
    );
  }

  private validarPaginacion(size: number): void {
    if (
      !Number.isInteger(size) ||
      size < HttpMetaMasivaRepository.PAGE_SIZE_MIN ||
      size > MAX_PAGE_SIZE
    ) {
      throw new Error(
        `size debe estar entre ${HttpMetaMasivaRepository.PAGE_SIZE_MIN} y ${MAX_PAGE_SIZE}.`
      );
    }
  }
}
